import { useState, useMemo } from "react";
import { ToolsShell, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Drill Bit Guide — pure lookup tool. Data sourced from the standard
// engineering tables used in carpentry/joinery (Machinery's Handbook,
// Family Handyman, and Bostich/Spax screw-mfg charts cross-checked).
//
// Pilot hole = the hole the threads bite into. Should be ~70% of the
// thread's minor diameter for hardwood, ~85% for softwood (lower means
// more grip; higher means easier driving with less splitting risk).
//
// Clearance hole = the hole the shank passes through (no thread bite).
// Typically ~10% larger than the shank diameter.
//
// We provide both Imperial (wood screws #4–#14) and metric (M3–M10).
// Drywall and metal entries are simpler (single recommendation).

const IMPERIAL_WOOD = [
  // [screw#, shankIn, hardwoodPilot, softwoodPilot, clearance]
  ["#4",  0.112, "5/64\"", "1/16\"", "9/64\""],
  ["#6",  0.138, "7/64\"", "5/64\"", "5/32\""],
  ["#8",  0.164, "1/8\"",  "3/32\"", "3/16\""],
  ["#10", 0.190, "9/64\"", "7/64\"", "7/32\""],
  ["#12", 0.216, "5/32\"", "1/8\"",  "1/4\""],
  ["#14", 0.250, "3/16\"", "9/64\"", "9/32\""],
];

const METRIC_WOOD = [
  // [size, hardwoodPilot, softwoodPilot, clearance]
  ["M3",   "2.0 mm", "1.8 mm", "3.5 mm"],
  ["M4",   "2.8 mm", "2.5 mm", "4.5 mm"],
  ["M5",   "3.5 mm", "3.2 mm", "5.5 mm"],
  ["M6",   "4.5 mm", "4.0 mm", "6.5 mm"],
  ["M8",   "6.0 mm", "5.5 mm", "8.5 mm"],
  ["M10",  "8.0 mm", "7.0 mm", "11.0 mm"],
];

const METAL = [
  // [size, tapDrill, clearance]
  ["#6-32",  "#36 (0.106\")", "#27 (0.144\")"],
  ["#8-32",  "#29 (0.136\")", "#18 (0.169\")"],
  ["#10-24", "#25 (0.149\")", "#9 (0.196\")"],
  ["#10-32", "#21 (0.159\")", "#9 (0.196\")"],
  ["1/4-20", "#7 (0.201\")",  "17/64 (0.266\")"],
  ["M3-0.5", "2.5 mm",        "3.4 mm"],
  ["M4-0.7", "3.3 mm",        "4.5 mm"],
  ["M5-0.8", "4.2 mm",        "5.5 mm"],
  ["M6-1.0", "5.0 mm",        "6.6 mm"],
  ["M8-1.25","6.8 mm",        "9.0 mm"],
];

const MATERIALS = [
  { id: "hardwood", label: "Hardwood (oak, maple, walnut)" },
  { id: "softwood", label: "Softwood (pine, fir, cedar)" },
  { id: "plywood", label: "Plywood / MDF" },
  { id: "drywall", label: "Drywall (with anchor)" },
  { id: "metal", label: "Metal (tapped hole)" },
];

const SIZE_SYS = [
  { id: "imperial", label: "Imperial (#4 – #14)" },
  { id: "metric", label: "Metric (M3 – M10)" },
];

export default function DrillBitGuide() {
  const [material, setMaterial] = useState("hardwood");
  const [sizeSystem, setSizeSystem] = useState("imperial");
  const [size, setSize] = useState("#8");

  // Re-default size when system changes
  const sizeOptions = useMemo(() => {
    if (material === "metal") return METAL.map((r) => r[0]);
    return sizeSystem === "imperial" ? IMPERIAL_WOOD.map((r) => r[0]) : METRIC_WOOD.map((r) => r[0]);
  }, [material, sizeSystem]);

  // Lookup result
  const result = useMemo(() => {
    if (material === "metal") {
      const row = METAL.find((r) => r[0] === size);
      if (!row) return null;
      return { pilot: row[1], pilotLabel: "Tap drill", clearance: row[2], notes: "Use cutting fluid. Tap by hand for small sizes, use a tap wrench, back off every half turn." };
    }
    if (material === "drywall") {
      return { pilot: "—", pilotLabel: "Pilot", clearance: "Anchor-specific", notes: "Drywall by itself won't hold a screw. Use a drywall anchor (self-drilling for light loads, toggle bolt for heavy). Anchor packaging specifies the drill size." };
    }
    const isImperial = sizeSystem === "imperial";
    const row = (isImperial ? IMPERIAL_WOOD : METRIC_WOOD).find((r) => r[0] === size);
    if (!row) return null;
    if (isImperial) {
      const [, , hardwoodPilot, softwoodPilot, clearance] = row;
      const pilot = material === "hardwood" ? hardwoodPilot : material === "softwood" ? softwoodPilot : softwoodPilot;
      const notes = material === "plywood" ? "Plywood splits less than solid wood — softwood pilot size works fine." : "";
      return { pilot, pilotLabel: "Pilot hole", clearance, notes };
    } else {
      const [, hardwoodPilot, softwoodPilot, clearance] = row;
      const pilot = material === "hardwood" ? hardwoodPilot : material === "softwood" ? softwoodPilot : softwoodPilot;
      const notes = material === "plywood" ? "Plywood splits less than solid wood — softwood pilot size works fine." : "";
      return { pilot, pilotLabel: "Pilot hole", clearance, notes };
    }
  }, [material, sizeSystem, size]);

  // If user switches material/system and current size isn't valid, snap to first.
  if (!sizeOptions.includes(size)) {
    setTimeout(() => setSize(sizeOptions[0]), 0);
  }

  const sel = {
    width: "100%", background: "#13141F", border: "1px solid #262839",
    borderRadius: 10, color: "#E8E9F3", fontSize: 15, padding: "12px 14px",
    boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 13, color: "#9A9DBE", display: "block", marginBottom: 6, marginTop: 14 };

  return (
    <ToolsShell title="Drill Bit Guide">
      <p style={{ fontSize: 14, color: "#C8CADF", marginTop: 0 }}>
        Pick a screw size and material; get the pilot and clearance bit sizes.
      </p>

      <label style={labelStyle}>Material</label>
      <select value={material} onChange={(e) => setMaterial(e.target.value)} style={sel}>
        {MATERIALS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
      </select>

      {material !== "metal" && material !== "drywall" && (
        <>
          <label style={labelStyle}>Size system</label>
          <div style={{ display: "flex", gap: 6 }}>
            {SIZE_SYS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSizeSystem(s.id)}
                style={{ background: sizeSystem === s.id ? TOOLS_ACCENT : "#1C1E2E", color: sizeSystem === s.id ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer", flex: 1 }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </>
      )}

      {material !== "drywall" && (
        <>
          <label style={labelStyle}>Screw size</label>
          <select value={size} onChange={(e) => setSize(e.target.value)} style={sel}>
            {sizeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </>
      )}

      {result && (
        <div style={{ marginTop: 22, background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 12, padding: "18px 20px" }}>
          <div style={{ display: "grid", gridTemplateColumns: result.pilot === "—" ? "1fr" : "1fr 1fr", gap: 14 }}>
            {result.pilot !== "—" && (
              <div>
                <div style={{ fontSize: 12, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>{result.pilotLabel}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>{result.pilot}</div>
              </div>
            )}
            <div>
              <div style={{ fontSize: 12, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Clearance / through</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>{result.clearance}</div>
            </div>
          </div>
          {result.notes && (
            <div style={{ marginTop: 14, padding: "10px 12px", background: "#0F1018", borderRadius: 8, fontSize: 13, color: "#C8CADF", borderLeft: `3px solid ${TOOLS_ACCENT}` }}>
              {result.notes}
            </div>
          )}
        </div>
      )}

      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 16 }}>
        Pilot sizes shown work for most projects. For end-grain or very hard species, go up one size. For exposed work, drill a countersink for a flush head. Data: standard fastener references (Machinery's Handbook, manufacturer charts).
      </p>
    </ToolsShell>
  );
}
